import { registerBrick, BrickContext, Brick } from 'olympe';
import { ReactBrick, useProperty, jsonToSxProps, cssToSxProps, ifNotNull, ifNotTransparent } from '@olympeio/core';
import { map, switchMap, combineLatestWith } from 'rxjs/operators';
import { of, combineLatest, Observable } from 'rxjs';
import React, { useState, useEffect, useRef, useCallback } from 'react';
import TextField from '@mui/material/TextField';
import CircularProgress from '@mui/material/CircularProgress';
import Paper from '@mui/material/Paper';
import Autocomplete from '@mui/material/Autocomplete';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { debounce } from '../helpers/_helpers';

/**
 * Provide a Dropdown visual component using MUI TextField
 */
export default class ActiveDirectoryDropdown extends ReactBrick {
    /**
     * @override
     */
    setupExecution($) {
        // Combine with Hidden property
        return combineLatest($.observe('Hidden'));
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @return {Observable<[Array<*>, number]>}
     */
    static getListObservable($) {
        // Observe the list size to retrigger an update if necessary
        const observeList = $.observe('Object List', false);
        const observeListSize = observeList.pipe(switchMap(list => {
            if (list === null) {
                return of(0);
            }
            return Array.isArray(list) ? of(list.length) : list.observeSize();
        }));

        const observeListAsArray = observeList.pipe(
            combineLatestWith(observeListSize),
            map(([list, size]) => {
                const array = list === null ? [] : list;
                return [array, size];
            }),
        );
        return observeListAsArray;
    }

    /**
     * @param {!BrickContext} $
     * @param {?Brick} objectToString
     * @param {Array} list
     * @return {Array<Observable>}
     */
    static runTransformer($, objectToString, list) {
        if (objectToString !== null) {
            const [objectInput] = objectToString.getInputs();
            const [stringOutput] = objectToString.getOutputs();
            return list.map(item => {
                const runner$ = $.runner(objectToString);
                return runner$
                    .set(stringOutput, item.toString()) // provide a default value to the output, so that we are sure the observable gets resolved
                    .set(objectInput, item)
                    .observe(stringOutput).pipe(map(label => ActiveDirectoryDropdown.createOption(item, label)));
            });
        } else {
            return list.map(item => $.observe(ActiveDirectoryDropdown.createOption(item, item.toString())));
        }
    }

    /**
     * Create an option JS Object readable for the Select and Autocomplete components
     *
     * @private
     * @param {Object} value
     * @param {string} label
     * @return {Object}
     */
    static createOption(value, label) {
        return {
            value: value,
            label: label
        };
    }

    /**
     * @override
     */
    updateParent(parent, element) {
        parent.style.overflow = 'visible'; // Allow overflow
        return super.updateParent(parent, element);
    }

    /**
     * @override
     */
    static getReactComponent($) {
        return (props) => {
            const [hidden] = props.values;
            return hidden ? null : ActiveDirectoryDropdown.autocompleteComponent($);
        };
    }

    /**
     * @private
     * @param {!BrickContext} $
     */
    static autocompleteComponent($) {
        const values = useProperty($, 'Selected Value');
        const autocompleteText = useProperty($, 'Autocomplete Text');
        const onInputChangeLambda = useProperty($, 'On Input Change');
        const loading = useProperty($, 'Is Loading');
        const label = useProperty($, 'Label');
        const height = useProperty($, 'Height');
        const overrideColor = useProperty($, 'Color');
        const [open, setOpen] = useState(false);
        const [focused, setFocused] = useState(false);
        const [options, setOptions] = useState([]);

        useEffect(() => {
            const observeListAsArray = ActiveDirectoryDropdown.getListObservable($);
            const observeOptions = observeListAsArray.pipe(
                switchMap(([values, size]) => {
                    if (size === 0) {
                        return of([]);
                    } else {
                        return $.observe('Object To String').pipe(
                            switchMap((objectToString) => {
                                return combineLatest(ActiveDirectoryDropdown.runTransformer($, objectToString, values));
                            })
                        );
                    }
                })
            );
            observeOptions.subscribe((data) => {
                setOptions(data);
            });
        }, []);

        // Create a unique sub-context for the onInputChange lambda function and destroy and recreate it only when the brick is changed.
        const onInputChangeContext = useRef();
        useEffect(() => {
            // Set the context in the ref object.
            onInputChangeContext.current = onInputChangeLambda ? $.runner(onInputChangeLambda) : null;
            // Destroy the sub-context when the onInputChangeLambda (the brick itself) is deleted/changed.
            return () => onInputChangeContext.current?.destroy();
        }, [onInputChangeLambda]);

        /**
         * Handle selected values
         *
         * @param {string} value
         * @return {Object}
         */
        const findOptionValue = (value) => {
            const foundOption = options.find(opt => opt.value === value);
            return foundOption !== undefined ? foundOption : null;
        };

        let value = values !== undefined ? findOptionValue(values) : null;
        const commonSx = {
            height: height,
            textOverflow: 'ellipsis',
        };

        /**
         * @param {*} params
         */
        const renderInput = (params) => {
            const isSelectedValue = values !== undefined && values !== null;
            const hasInput = isSelectedValue || autocompleteText !== '';
            const props = ActiveDirectoryDropdown.getTextFieldProps($, hasInput, params);
            const inputProps = {
                ...props.InputProps,
                endAdornment: (
                    <React.Fragment>
                        {loading && open ? (
                            <CircularProgress
                                color="inherit"
                                size={20}
                            />
                        ) : null}
                        {params.InputProps.endAdornment}
                    </React.Fragment>
                ),
                onBlur: () => {
                    setFocused(false);
                }
            };

            return (
                <TextField
                    {...params}
                    {...props}
                    InputProps={inputProps}
                    focused={focused}
                    onFocus={() => {
                        setFocused(true);
                    }}
                    inputRef={(input) => {
                        if (focused && input) {
                            input.focus();
                        }
                    }}
                />
            );
        };

        /**
         * @param {*} event
         * @param {string} newValue
         */
        const onInputChange = (event, newValue) => {
            if (event !== null) {
                $.set('Autocomplete Text', newValue || '');
                const isSelectEvent = event.type === 'click';
                if (onInputChangeLambda && ((isSelectEvent && newValue === '') || !isSelectEvent)) {
                    _setInputChange(newValue);
                }
            }
            setOpen(true);
            setFocused(true);
        };

        /**
         * Debounce input change by waiting to get the last value after user typing
         * @param {string} newValue
         */
         const _setInputChange = useCallback(
            debounce((newValue) => {
                if (onInputChangeLambda && onInputChangeContext && onInputChangeContext.current) {
                    const [startInput, textInput] = onInputChangeLambda.getInputs();
                    // Set new input values to the onInputChange context.
                    onInputChangeContext.current.set(textInput, newValue).trigger(startInput);
                }
            }, 150),
            [onInputChangeLambda, onInputChangeContext]
        );

        /**
         * @param {*} event
         * @param {Object|Array<Object>} currentValues
         * @param {string} reason
         */
        const onChange = (event, currentValues, reason) => {
            if (reason === 'clear') {
                setOpen(false);
                setFocused(false);
            }
            ActiveDirectoryDropdown.onChangeCallbackAutocomplete($, currentValues);
        };

        const theme = createTheme({
            palette: {
                primary: {
                    main: overrideColor === undefined ? '#1976D2' : overrideColor.toHexString(),
                }
            },
            components: {
                MuiAutocomplete: {
                    styleOverrides: {
                        root: {
                            height: '100%',
                        },
                        endAdornment: {
                            right: '7px !important',
                        },
                    },
                },
                MuiFormControl: {
                    styleOverrides: {
                        root: {
                            height: '100%',
                        },
                    },
                },
            },
        });
        // Element
        return (
            <ThemeProvider theme={theme}>
                <Autocomplete
                    // Props
                    value={value}
                    options={options}
                    inputValue={autocompleteText || ''}
                    openOnFocus={true}
                    loading={loading && open}
                    open={open}
                    PaperComponent={(params) => (
                        <ActiveDirectoryDropdown.CustomPaper
                            $={$}
                            params={params}
                        />
                    )}
                    renderInput={renderInput}
                    // needed to fetch new options on typing instead of filtering the presented ones
                    // https://mui.com/material-ui/react-autocomplete/#search-as-you-type
                    filterOptions={(x) => x}
                    // events
                    onChange={onChange}
                    onInputChange={onInputChange}
                    onClose={() => {
                        setOpen(false);
                    }}
                    onOpen={() => {
                        setOpen(true);
                    }}
                    // needs to maintain the height of the brick for different variants of autoselect
                    sx={{
                        // Outline variant
                        '.MuiOutlinedInput-root.MuiInputBase-root': {
                            padding: '6px 65px 6px 6px',
                            ...commonSx,
                        },
                        // Standard variant
                        '.MuiInput-underline': {
                            '&.MuiInputBase-root:not(.Mui-focused)': {
                                ...commonSx,
                            }
                        },
                        // Filled variant
                        '.MuiFilledInput-root': {
                            '&.MuiInputBase-root': {
                                paddingTop: label ? '15px' : '0px',
                                paddingRight: '60px',
                                ...commonSx,
                            },
                        },
                    }}
                />
            </ThemeProvider>
        );
    }

    /**
     * @private
     * @param {!BrickContext} $
     * @param {boolean} hasInput
     * @param {{InputProps: {}, InputLabelProps: {}}} params
     * @return {Object}
     */
    static getTextFieldProps($, hasInput, params = { InputProps: {}, InputLabelProps: {} }) {
        const label = useProperty($, 'Label');
        const emptyText = useProperty($, 'Empty Text');
        const fontFamily = useProperty($, 'Font Family');
        const borderWidth = useProperty($, 'Border Width');
        const borderColor = useProperty($, 'Border Color');
        const borderRadius = useProperty($, 'Border Radius');
        const showBorder = borderWidth > 0 && borderColor.toHexString() !== '#00000000';
        const textColorOverride = useProperty($, 'Text Color Override');
        const shrink = (hasInput || emptyText !== '') && label !== '';

        return {
            // MUI Properties
            label: typeof label === 'string' ? label.trim() : '',
            variant: useProperty($, 'Variant'),
            placeholder: emptyText,
            helperText: useProperty($, 'Helper Text'),
            fontFamily: fontFamily,
            size: 'small',

            // custom styling
            InputProps: {
                ...params.InputProps,
                sx: {
                    flex: 'auto',
                    fontFamily: fontFamily,
                    tabIndex: useProperty($, 'Tab Index'),
                    ...ifNotTransparent('backgroundColor', useProperty($, 'Default Color')),
                    ...ifNotNull('borderRadius', `${borderRadius}px`, borderRadius),
                    ...ifNotNull('borderWidth', borderWidth, showBorder),
                    ...ifNotNull('borderStyle', 'solid', showBorder),
                    ...ifNotNull('boxSizing', 'border-box', showBorder),
                    ...ifNotTransparent('color', textColorOverride)
                }
            },
            FormHelperTextProps: {
                sx: {
                    fontFamily: fontFamily,
                    ...ifNotTransparent('color', borderColor)
                }
            },
            InputLabelProps: {
                ...params.InputLabelProps,
                shrink: shrink,
                sx: {
                    fontFamily: fontFamily,
                }
            },
            sx: {
                width: 1,
                height: 1,
            }
        };
    }

    /**
     * On change callback when using Autocomplete component
     *
     * @private
     * @param {!BrickContext} $
     * @param {?string|Object|Array<!*>} newValue
     */
    static onChangeCallbackAutocomplete($, newValue) {
        const selectedValue = (typeof newValue === 'string' || newValue === null)
            ? newValue : newValue.value;
        $.set('Selected Value', selectedValue);
        $.trigger('On Change');
    }
}

/**
 * @param {!BrickContext} $
 * @param {*} params
 */
 ActiveDirectoryDropdown.CustomPaper = ({ $, params }) => {
    return (
        <Paper
            {...params}
            sx={{
                ...cssToSxProps(useProperty($, 'CSS Property')),
                ...jsonToSxProps(useProperty($, 'MUI sx [json]')),
            }}
        />
    );
};

registerBrick('01815b803eb71935311e', ActiveDirectoryDropdown);
